/**
 * Contractor Job Detail - Field Execution Screen
 * 
 * Mobile-first, action-first, compliance-enforced
 * Principle: Instantly know where to go, what to do, how to complete
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MapPin, Upload, Camera, ArrowLeft, Phone, FileText,
  CheckCircle, AlertTriangle, Clock, Loader2, X, Image,
  ChevronRight, Zap, Check, Navigation
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({ baseURL: `${API_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('contractor_portal_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ============================================================
// 🎨 DESIGN TOKENS - ENTERPRISE TEAL THEME
// ============================================================
const tokens = {
  primary: "#0d9488",      // teal-600
  primaryDark: "#0f766e",  // teal-700
  bg: "#f8fafc",           // slate-50
  card: "#FFFFFF",
  textMain: "#0f172a",     // slate-900
  textMuted: "#64748b",    // slate-500
  border: "#e2e8f0",       // slate-200
  success: "#10B981",
  warning: "#F59E0B",
  error: "#EF4444",
};

// ============================================================
// 🧱 COMPONENTS
// ============================================================

const Card = ({ children, className = "" }) => (
  <div className={`bg-white border-2 border-slate-200 rounded-2xl ${className}`}>
    {children}
  </div>
);

const PrimaryButton = ({ children, onClick, loading, disabled, className = "", variant = "primary" }) => {
  const variants = {
    primary: "bg-teal-700 hover:bg-teal-800 text-white",
    success: "bg-emerald-500 hover:bg-emerald-600 text-white",
    warning: "bg-amber-500 hover:bg-amber-600 text-white",
  };

  return (
    <motion.button
      whileHover={!loading && !disabled ? { scale: 1.01 } : {}}
      whileTap={!loading && !disabled ? { scale: 0.98 } : {}}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        w-full py-3.5 rounded-xl font-semibold text-base transition-all
        flex items-center justify-center gap-2 shadow-md
        ${variants[variant]}
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        ${className}
      `}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          Processing...
        </span>
      ) : (
        children
      )}
    </motion.button>
  );
};

const UploadButton = ({ icon: Icon, label, files, onChange, accept, multiple = false }) => {
  const hasFiles = files && (Array.isArray(files) ? files.length > 0 : files);
  
  return (
    <label className={`
      flex items-center justify-between p-4 rounded-xl border-2 border-dashed cursor-pointer
      transition-all
      ${hasFiles 
        ? "border-emerald-300 bg-emerald-50" 
        : "border-slate-200 hover:border-teal-400 hover:bg-teal-50"
      }
    `}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${hasFiles ? "bg-emerald-100" : "bg-gray-100"}`}>
          <Icon className={`h-5 w-5 ${hasFiles ? "text-emerald-600" : "text-slate-500"}`} />
        </div>
        <div>
          <div className="font-medium text-slate-900 text-sm">{label}</div>
          <div className="text-xs text-slate-500">
            {hasFiles 
              ? (Array.isArray(files) ? `${files.length} file(s) selected` : files.name)
              : "Tap to upload"
            }
          </div>
        </div>
      </div>
      {hasFiles && <CheckCircle className="h-5 w-5 text-emerald-500" />}
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        hidden
        onChange={onChange}
      />
    </label>
  );
};

const StatusBadge = ({ status }) => {
  const statusMap = {
    assigned: { label: "Assigned", className: "bg-blue-100 text-blue-700", icon: Clock },
    sent: { label: "Pending", className: "bg-blue-100 text-blue-700", icon: Clock },
    accepted: { label: "Accepted", className: "bg-emerald-100 text-emerald-700", icon: Check },
    awaiting_quote: { label: "Quote Needed", className: "bg-amber-100 text-amber-700", icon: FileText },
    quote_submitted: { label: "Quote Sent", className: "bg-purple-100 text-purple-700", icon: FileText },
    approved: { label: "Ready to Start", className: "bg-emerald-100 text-emerald-700", icon: Zap },
    in_progress: { label: "In Progress", className: "bg-cyan-100 text-cyan-700", icon: Loader2 },
    completed: { label: "Completed", className: "bg-green-100 text-green-700", icon: CheckCircle },
  };

  const config = statusMap[status] || { label: status, className: "bg-gray-100 text-gray-700", icon: Clock };
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
      <Icon className="h-3.5 w-3.5" />
      {config.label}
    </span>
  );
};

// ============================================================
// 🎯 MAIN COMPONENT
// ============================================================

export default function ContractorJobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [jobSheet, setJobSheet] = useState(null);
  const [notes, setNotes] = useState("");
  const [quoteAmount, setQuoteAmount] = useState("");

  // Load job data
  const loadJob = useCallback(async () => {
    try {
      const res = await api.get(`/contractor-company/jobs/${jobId}`);
      const jobData = res.data.data || res.data;
      setJob(jobData);
      setNotes(jobData?.contractor_notes || "");
      
      // Load existing evidence if assignment exists
      const assignmentId = jobData?.assignment_id || jobData?.contractor_assignment_id;
      if (assignmentId) {
        try {
          const evidenceRes = await axios.get(
            `${API_URL}/api/wyldecrest/jobs/${jobId}/assignments/${assignmentId}/evidence`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('contractor_portal_token')}` }}
          );
          
          const evidence = evidenceRes.data;
          if (evidence.photos?.length > 0) {
            // Mark as already uploaded (from server)
            setPhotos(evidence.photos.map(p => ({ ...p, uploaded: true })));
          }
          if (evidence.job_sheet) {
            setJobSheet({ ...evidence.job_sheet, uploaded: true });
          }
        } catch (evidenceErr) {
          console.log("Evidence not available yet:", evidenceErr.message);
        }
      }
    } catch (err) {
      console.error("Load error:", err);
      toast.error("Failed to load job");
      if (err.response?.status === 401) {
        navigate('/contractor-company/login');
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, navigate]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  // Get action based on status
  const getAction = () => {
    if (!job) return null;
    
    switch (job.status) {
      case "assigned":
      case "sent":
        return { label: "Accept Job", action: "accept", variant: "primary" };
      case "awaiting_quote":
      case "quote_requested":
        return { label: "Submit Quote", action: "quote", variant: "warning" };
      case "approved":
        return { label: "Start Job", action: "start", variant: "success" };
      case "in_progress":
        return { label: "Complete Job", action: "complete", variant: "success" };
      default:
        return null;
    }
  };

  const actionConfig = getAction();

  // Check if completion is blocked - considers both local and uploaded evidence
  const hasPhotos = photos.length > 0;
  const hasJobSheet = !!jobSheet;
  const canComplete = job?.status === "in_progress" && hasPhotos && hasJobSheet;

  // Handle primary action
  const handleAction = async () => {
    if (!actionConfig) return;
    
    // Block completion if missing evidence
    if (actionConfig.action === "complete" && !canComplete) {
      toast.error("Upload photos and job sheet to complete");
      return;
    }

    // Validate quote amount
    if (actionConfig.action === "quote" && (!quoteAmount || parseFloat(quoteAmount) <= 0)) {
      toast.error("Enter a valid quote amount");
      return;
    }

    setActionLoading(true);

    try {
      let endpoint = "";
      let payload = {};

      switch (actionConfig.action) {
        case "accept":
          endpoint = `/contractor-company/jobs/${jobId}/accept`;
          break;
        case "quote":
          endpoint = `/contractor-company/jobs/${jobId}/quote`;
          payload = { amount: parseFloat(quoteAmount), notes };
          break;
        case "start":
          endpoint = `/contractor-company/jobs/${jobId}/start`;
          break;
        case "complete":
          // Use assignment-based endpoint if available
          const completeAssignmentId = job?.assignment_id || job?.contractor_assignment_id;
          
          if (completeAssignmentId) {
            // Upload any remaining local files first
            const localPhotos = photos.filter(p => !p.uploaded);
            const localJobSheet = jobSheet && !jobSheet.uploaded ? jobSheet : null;
            
            if (localPhotos.length > 0) {
              const photoFormData = new FormData();
              localPhotos.forEach(photo => photoFormData.append('files', photo));
              await axios.post(
                `${API_URL}/api/wyldecrest/jobs/${jobId}/assignments/${completeAssignmentId}/photos`,
                photoFormData,
                { headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${localStorage.getItem('contractor_portal_token')}` }}
              );
            }
            
            if (localJobSheet) {
              const sheetFormData = new FormData();
              sheetFormData.append('file', localJobSheet);
              await axios.post(
                `${API_URL}/api/wyldecrest/jobs/${jobId}/assignments/${completeAssignmentId}/job-sheet`,
                sheetFormData,
                { headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${localStorage.getItem('contractor_portal_token')}` }}
              );
            }
            
            // Now complete via Wyldecrest endpoint (enforced)
            await axios.post(
              `${API_URL}/api/wyldecrest/jobs/${jobId}/assignments/${completeAssignmentId}/complete`,
              { notes, completed_by: "contractor" },
              { headers: { 'Authorization': `Bearer ${localStorage.getItem('contractor_portal_token')}` }}
            );
          } else {
            // Fallback to contractor-company endpoint
            const formData = new FormData();
            photos.forEach((photo) => {
              if (!photo.uploaded) formData.append('photos', photo);
            });
            if (jobSheet && !jobSheet.uploaded) formData.append('job_sheet', jobSheet);
            formData.append('notes', notes);
            
            await api.post(`/contractor-company/jobs/${jobId}/complete`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          }
          
          toast.success("Job completed!", { icon: "🎉" });
          navigate('/contractor-company/dashboard');
          return;
        default:
          return;
      }

      await api.post(endpoint, payload);
      toast.success(`${actionConfig.label} successful!`, { icon: "✅" });
      loadJob();
    } catch (err) {
      console.error("Action error:", err);
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle photo upload - uploads to assignment if available
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // If we have an assignment ID, upload to Wyldecrest endpoint
    const assignmentId = job?.assignment_id || job?.contractor_assignment_id;
    
    if (assignmentId) {
      try {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        
        await axios.post(
          `${API_URL}/api/wyldecrest/jobs/${jobId}/assignments/${assignmentId}/photos`,
          formData,
          { 
            headers: { 
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('contractor_portal_token')}`
            }
          }
        );
        
        toast.success(`${files.length} photo(s) uploaded`, { icon: "📷" });
        loadJob(); // Refresh to get updated photos list
      } catch (err) {
        console.error("Photo upload error:", err);
        // Fallback to local state if API fails
        setPhotos(prev => [...prev, ...files]);
        toast.success(`${files.length} photo(s) added locally`);
      }
    } else {
      // No assignment - store locally for now
      setPhotos(prev => [...prev, ...files]);
      toast.success(`${files.length} photo(s) added`);
    }
  };

  // Handle job sheet upload - uploads to assignment if available
  const handleJobSheetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const assignmentId = job?.assignment_id || job?.contractor_assignment_id;
    
    if (assignmentId) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        await axios.post(
          `${API_URL}/api/wyldecrest/jobs/${jobId}/assignments/${assignmentId}/job-sheet`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('contractor_portal_token')}`
            }
          }
        );
        
        toast.success("Job sheet uploaded", { icon: "📄" });
        loadJob();
      } catch (err) {
        console.error("Job sheet upload error:", err);
        setJobSheet(file);
        toast.success("Job sheet added locally");
      }
    } else {
      setJobSheet(file);
      toast.success("Job sheet added");
    }
  };

  // Open directions
  const openDirections = () => {
    const address = job?.address || job?.postcode || `${job?.park_name} ${job?.plot}`;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-slate-50 p-5">
        <Card className="p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          <p className="text-slate-900 font-medium">Job not found</p>
          <button 
            onClick={() => navigate('/contractor-company/jobs')}
            className="mt-4 text-teal-700 text-sm font-medium"
          >
            ← Back to jobs
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ═══════════════════════════════════════════════════════
          HEADER
      ═══════════════════════════════════════════════════════ */}
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50">
        <div className="px-4 py-3 flex items-center gap-3">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </motion.button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900 truncate">{job.job_reference}</h1>
            <p className="text-xs text-slate-500 truncate">
              {job.park_name || job.site_name} {job.plot && `• Plot ${job.plot}`}
            </p>
          </div>
          <StatusBadge status={job.status} />
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════
          CONTENT
      ═══════════════════════════════════════════════════════ */}
      <main className="p-4 space-y-4 pb-32">
        
        {/* 🔥 ACTION BLOCK */}
        {actionConfig && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-2xl p-5 border-2 ${
              actionConfig.variant === "success" 
                ? "bg-gradient-to-br from-emerald-50 to-emerald-100/30 border-emerald-200"
                : actionConfig.variant === "warning"
                ? "bg-gradient-to-br from-amber-50 to-amber-100/30 border-amber-200"
                : "bg-gradient-to-br from-teal-50 to-teal-100/30 border-teal-300"
            }`}
          >
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-1">
              <Zap className="h-3 w-3" /> Next Action
            </div>
            <div className={`text-xl font-bold mb-4 ${
              actionConfig.variant === "success" ? "text-emerald-700" :
              actionConfig.variant === "warning" ? "text-amber-700" :
              "text-teal-700"
            }`}>
              {actionConfig.label}
            </div>

            {/* Quote Input */}
            {actionConfig.action === "quote" && (
              <div className="mb-4">
                <label className="text-sm font-medium text-slate-900 block mb-2">Quote Amount (£)</label>
                <input
                  type="number"
                  value={quoteAmount}
                  onChange={(e) => setQuoteAmount(e.target.value)}
                  placeholder="Enter amount..."
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-lg font-semibold focus:border-teal-500 outline-none"
                />
              </div>
            )}

            {/* Completion Requirements */}
            {actionConfig.action === "complete" && (
              <div className="mb-4 p-3 bg-white/70 rounded-xl">
                <div className="text-xs font-medium text-slate-500 mb-2">Required to complete:</div>
                <div className="space-y-1">
                  <div className={`text-sm flex items-center gap-2 ${photos.length > 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {photos.length > 0 ? <CheckCircle className="h-4 w-4" /> : <X className="h-4 w-4" />}
                    Photos uploaded ({photos.length})
                  </div>
                  <div className={`text-sm flex items-center gap-2 ${jobSheet ? "text-emerald-600" : "text-red-500"}`}>
                    {jobSheet ? <CheckCircle className="h-4 w-4" /> : <X className="h-4 w-4" />}
                    Job sheet uploaded
                  </div>
                </div>
              </div>
            )}

            <PrimaryButton
              onClick={handleAction}
              loading={actionLoading}
              disabled={actionConfig.action === "complete" && !canComplete}
              variant={actionConfig.variant}
            >
              {actionConfig.label}
              <ChevronRight className="h-5 w-5" />
            </PrimaryButton>
          </motion.div>
        )}

        {/* LOCATION + MAP */}
        <Card className="overflow-hidden">
          <div className="h-[180px] bg-gray-200 relative">
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(job.address || job.postcode || `${job.park_name} ${job.plot}`)}`}
              className="w-full h-full border-0"
              loading="lazy"
              allowFullScreen
            />
          </div>
          <div className="p-4">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-900 font-medium">
                  {job.park_name || job.site_name}
                </p>
                <p className="text-sm text-slate-500">
                  {job.address || `Plot ${job.plot}`}
                </p>
                {job.postcode && (
                  <p className="text-sm text-slate-500">{job.postcode}</p>
                )}
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={openDirections}
              className="mt-3 w-full py-2.5 border-2 border-teal-600 text-teal-700 rounded-xl font-medium text-sm flex items-center justify-center gap-2 hover:bg-teal-50 transition-colors"
            >
              <Navigation className="h-4 w-4" />
              Get Directions
            </motion.button>
          </div>
        </Card>

        {/* JOB DETAILS */}
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Job Details</h3>
          <p className="text-sm text-slate-500 leading-relaxed">{job.description}</p>
          
          <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-200">
            <div>
              <div className="text-xs text-slate-500">Trade</div>
              <div className="text-sm font-medium text-slate-900">{job.trade || "General"}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Priority</div>
              <div className={`text-sm font-medium ${
                job.priority === "Emergency" ? "text-red-600" :
                job.priority === "Urgent" ? "text-amber-600" :
                "text-slate-900"
              }`}>{job.priority || "Normal"}</div>
            </div>
            {job.target_date && (
              <div>
                <div className="text-xs text-slate-500">Target Date</div>
                <div className="text-sm font-medium text-slate-900">
                  {new Date(job.target_date).toLocaleDateString("en-GB")}
                </div>
              </div>
            )}
            {job.quote?.total && (
              <div>
                <div className="text-xs text-slate-500">Approved Quote</div>
                <div className="text-sm font-bold text-emerald-600">£{job.quote.total}</div>
              </div>
            )}
          </div>
        </Card>

        {/* UPLOAD SECTION */}
        {["approved", "in_progress"].includes(job.status) && (
          <Card className="p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Upload Evidence</h3>
            
            <div className="space-y-3">
              <UploadButton
                icon={Camera}
                label="Photos"
                files={photos}
                onChange={handlePhotoUpload}
                accept="image/*"
                multiple
              />
              
              <UploadButton
                icon={FileText}
                label="Job Sheet"
                files={jobSheet}
                onChange={handleJobSheetUpload}
                accept=".pdf,.doc,.docx,image/*"
              />
            </div>

            {/* Photo Preview */}
            {photos.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {photos.map((photo, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                    <img 
                      src={URL.createObjectURL(photo)} 
                      alt={`Photo ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {job.status === "in_progress" && (!photos.length || !jobSheet) && (
              <div className="mt-3 flex items-center gap-2 text-amber-600 text-xs bg-amber-50 p-3 rounded-xl">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                Upload photos + job sheet to complete this job
              </div>
            )}
          </Card>
        )}

        {/* NOTES */}
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Notes</h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this job..."
            className="w-full min-h-[100px] p-3 border-2 border-slate-200 rounded-xl text-sm resize-none focus:border-teal-500 outline-none"
          />
        </Card>

        {/* Contact */}
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Need Help?</h3>
          <motion.a
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            href="tel:+441onal234567890"
            className="flex items-center gap-3 p-3 bg-teal-50 rounded-xl hover:bg-teal-100 transition-colors"
          >
            <div className="p-2 bg-teal-700 rounded-lg">
              <Phone className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-medium text-slate-900">Call Helpdesk</div>
              <div className="text-xs text-slate-500">Available 24/7</div>
            </div>
          </motion.a>
        </Card>
      </main>
    </div>
  );
}
